import { FC } from 'react';
import { CheckCircle2, Clock, XCircle, AlertCircle } from 'lucide-react';
import { UserProjectStatus, type UserProject } from '@teachur/http-client';
import { SubmissionItem } from './SubmissionItem';
import { SubmissionList } from '../../SubmissionList/SubmissionList';

interface ProjectSubmissionsProps {
  userProjects: UserProject[];
}

const getStatusBadge = (status: UserProjectStatus) => {
  const statusConfig = {
    [UserProjectStatus.Pending]: {
      icon: Clock,
      text: 'Pending Review',
      bgColor: 'bg-[#FFF9EE]',
      textColor: 'text-[#FE9B0E]',
    },
    [UserProjectStatus.InReview]: {
      icon: AlertCircle,
      text: 'In Review',
      bgColor: 'bg-[#E6F7EF]',
      textColor: 'text-[#47B881]',
    },
    [UserProjectStatus.Approved]: {
      icon: CheckCircle2,
      text: 'Approved',
      bgColor: 'bg-[#E6F7EF]',
      textColor: 'text-[#47B881]',
    },
    [UserProjectStatus.NotYet]: {
      icon: XCircle,
      text: 'Needs Revision',
      bgColor: 'bg-[#FEE]',
      textColor: 'text-[#E53E3E]',
    },
    not_yet: {
      icon: XCircle,
      text: 'Needs Revision',
      bgColor: 'bg-[#FEE]',
      textColor: 'text-[#E53E3E]',
    },
  };

  const config = statusConfig[status];
  const Icon = config.icon;

  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${config.bgColor}`}>
      <Icon size={16} className={config.textColor} />
      <span className={`[font-size:var(--fs-l5)]! font-semibold ${config.textColor}`}>
        {config.text}
      </span>
    </div>
  );
};

export const ProjectSubmissions: FC<ProjectSubmissionsProps> = ({ userProjects }) => {
  const submissions = userProjects
    .flatMap((userProject: UserProject) =>
      (userProject.submissions ?? []).map((submission) => ({ userProject, submission }))
    )
    .sort(
      (a, b) =>
        new Date(b.submission.createdAt).getTime() - new Date(a.submission.createdAt).getTime()
    );

  if (submissions.length === 0) {
    return null;
  }

  return (
    <SubmissionList title="Your Submissions">
      {submissions.map(({ userProject, submission }) => (
        <SubmissionItem
          key={submission.id}
          userProject={userProject}
          submission={submission}
          statusBadge={getStatusBadge(submission.status as unknown as UserProjectStatus)}
        />
      ))}
    </SubmissionList>
  );
};
